//
// displayStore
//
import PropTypes from 'prop-types';
import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useReducer,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  appLinks,
  displayJson,
} from '../../utils';

import DisplayContext from './DisplayContext';

// Smallest layout the dashboard shell is designed for — Dashboard.jsx carries
// the matching min-w/min-h classes so smaller windows scroll instead of clipping
// Single source of truth for the shell's minimum layout box (px) and how far auto-zoom
// may shrink to reach it. Consumed by computeAutoZoom, shellFitsWindow, and shellZoomStyle
// (which carries the shell's min box) — nothing else should hardcode these numbers.
const minLayoutWidthPx = 1024;
const minLayoutHeightPx = 740;
const minAutoZoom = 0.8;
const zoomStorageKey = 'pxDisplayZoom';
// Only the public pages can meet a window this narrow: the shell's min layout is wider
const narrowWidthPx = 640;

// Standardized zoom only (gBCR reflects it): legacy WebKit (iPadOS 17) lays zoom out but ignores
// runtime changes to it — it must fail this probe so those devices fall back to scrolling.
const detectZoomPaintSupport = () => {
  try {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;width:100px;height:10px;zoom:0.5';
    document.body.appendChild(probe);
    const paintedWidth = probe.getBoundingClientRect().width;
    document.body.removeChild(probe);
    return ((paintedWidth > 40) && (paintedWidth < 60));
  } catch {
    return false;
  }
};
const zoomSupported = detectZoomPaintSupport();

const readStoredZoomSetting = () => {
  const stored = parseFloat(localStorage.getItem(zoomStorageKey));
  return ((Number.isFinite(stored)) && (stored >= 0.5) && (stored <= 1.5)) ? stored : 'auto';
};

// Largest 0.05 step fitting the min layout in the window, floored to keep text legible.
// Inputs are window dimensions, which the shell zoom cannot affect: no feedback loop.
const computeAutoZoom = (width, height) => {
  const fit = Math.min(1, width / minLayoutWidthPx, height / minLayoutHeightPx);
  return Math.max(minAutoZoom, Math.floor((fit * 20) + 0.001) / 20);
};

const shellFitsIn = (width, height, zoom) => ((width >= (minLayoutWidthPx * zoom))
  && (height >= (minLayoutHeightPx * zoom)));

const initialState = {
  browserHeight: window.innerHeight,
  browserWidth: window.innerWidth,
  visualViewportHeight: window.visualViewport?.height ?? window.innerHeight,
  overlay: undefined,
};

const stateReducer = (state, action) => {
  // console.log(`displayStore stateReducer action=${displayJson(action)}`);
  switch (action.type) {
    case 'setDisplaySize':
      return {
        ...state,
        browserHeight: action.height,
        browserWidth: action.width,
      };

    case 'setVisualViewportHeight':
      if (state.visualViewportHeight === action.height) return state;

      return {
        ...state,
        visualViewportHeight: action.height,
      };

    case 'setOverlay': {
      const newState = {
        ...state,
        overlay: action.overlay,
      };

      return newState;
    }

    default:
      throw new Error(`displayStore stateReducer action=${
        displayJson(action)}`);
  }
};

function DisplayProvider(props) {
  const {
    children,
  } = props;
  const [subMenu, setSubMenu] = useState();
  const [zoomSetting, setZoomSettingState] = useState(readStoredZoomSetting);
  const [demo, setDemo] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState(null);
  const demoCntRef = useRef(0);
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(stateReducer, initialState);

  const setOverlay = useCallback(
    (overlay) => {
      dispatch({
        type: 'setOverlay',
        overlay,
      });
    },
    [],
  );

  // 'auto' or a number; the zoom is a per-device comfort setting, so it lives
  // in localStorage rather than in the account settings
  const setZoomSetting = useCallback(
    (newSetting) => {
      if (newSetting === 'auto') {
        localStorage.removeItem(zoomStorageKey);
      } else {
        localStorage.setItem(zoomStorageKey, `${newSetting}`);
      }
      setZoomSettingState(newSetting);
    },
    [],
  );

  // Monitor display coordinates
  // as well as browseer overall size
  //
  const getDisplaySize = useCallback(
    () => {
      // Layout viewport only: the app lays out against it, and unlike the visual
      // viewport it is immune to pinch/page zoom (Safari page zoom would inflate
      // visualViewport and skew the zoom fit) and to our own shell zoom.
      dispatch({
        type: 'setDisplaySize',
        height: window.innerHeight,
        width: window.innerWidth,
      });
    },
    [],
  );

  useEffect(
    () => {
      getDisplaySize();
    },
    [getDisplaySize],
  );

  useEffect(
    () => {
      window.addEventListener('resize', getDisplaySize);
      return () => {
        window.removeEventListener('resize', getDisplaySize);
      };
    },
    [getDisplaySize],
  );

  // The on-screen keyboard shrinks the visual viewport but not innerHeight on iOS. Kept apart
  // from the sizing state above: the zoom fit must stay off visualViewport, which Safari page
  // zoom inflates.
  const getVisualViewportHeight = useCallback(
    () => {
      dispatch({
        type: 'setVisualViewportHeight',
        height: window.visualViewport?.height ?? window.innerHeight,
      });
    },
    [],
  );

  useEffect(
    () => {
      getVisualViewportHeight();

      const { visualViewport } = window;

      if (!visualViewport) {
        window.addEventListener('resize', getVisualViewportHeight);
        return () => {
          window.removeEventListener('resize', getVisualViewportHeight);
        };
      }

      visualViewport.addEventListener('resize', getVisualViewportHeight);
      return () => {
        visualViewport.removeEventListener('resize', getVisualViewportHeight);
      };
    },
    [getVisualViewportHeight],
  );

  const setSubMenuCall = useCallback(
    (newSubMenu) => {
      // console.log(`setSub ${newSubMenu}`);
      setSubMenu(newSubMenu);
    },
    [],
  );

  const setDemoMode = useCallback(
    (info) => {
      setDemo(info);
      demoCntRef.current = 0;
      // Use void to make it clear we do not want to wait this async function:
      void navigate(appLinks.casesStats({ zoom: 7 }));
    },
    [navigate],
  );

  useEffect(
    () => {
      if (demo) {
        const timer = setInterval(
          () => {
            const demoCnt = demoCntRef.current;

            console.log(`demo was ${demoCnt}`);

            if (demoCnt === 0) {
              demoCntRef.current = 1;
              // Use void to make it clear we do not want to wait this async function:
              void navigate(appLinks.casesList());
            } else if (demoCnt === 1) {
              demoCntRef.current = 2;
              // Use void to make it clear we do not want to wait this async function:
              void navigate(appLinks.pollResults({
                pollId: demo.pollId,
                pollItemId: demo.pollItemId,
                zoom: 6,
              }));
            } else if (demoCnt === 2) {
              demoCntRef.current = 0;
              // Use void to make it clear we do not want to wait this async function:
              void navigate(appLinks.casesStats({ zoom: 7 }));
            }

            console.log(`now ${demoCntRef.current}`);
          },
          1000 * (demo.periodS || 5),
        );

        return () => {
          clearTimeout(timer);
        };
      }

      return undefined;
    },
    [demo, navigate],
  );

  const contextValue = useMemo(
    () => {
      const zoomAuto = (zoomSupported)
        ? computeAutoZoom(state.browserWidth, state.browserHeight)
        : 1;
      const zoom = ((!zoomSupported) || (zoomSetting === 'auto')) ? zoomAuto : zoomSetting;

      // The shell's min box, in its own (pre-zoom) px; keeps the shell at least this big so
      // #root scrolls once the window is too small for the min layout even at minAutoZoom.
      const shellMinBox = {
        minWidth: `${minLayoutWidthPx}px`,
        minHeight: `${minLayoutHeightPx}px`,
      };

      const ret = {
        display: {
          // Layout px available to the (possibly zoomed) app — what sizing math should use
          browserHeight: Math.round(state.browserHeight / zoom),
          browserWidth: Math.round(state.browserWidth / zoom),
          // Device px of the window, for diagnostics
          browserHeightRaw: state.browserHeight,
          browserWidthRaw: state.browserWidth,
          zoom,
          zoomAuto,
          zoomSetting,
          zoomSupported,
          setZoomSetting,
          // Applied by Dashboard.jsx on the shell: inverse sizing keeps the zoomed shell filling the window.
          // Ceil px (not calc(100%/zoom)) so the scaled shell lands a sub-pixel OVER the window and covers it
          // edge to edge with no white seam; Dashboard clips that <1px overshoot via #root overflow:hidden.
          shellZoomStyle: (zoom === 1)
            ? shellMinBox
            : {
                ...shellMinBox,
                zoom,
                width: `${Math.ceil(state.browserWidth / zoom)}px`,
                height: `${Math.ceil(state.browserHeight / zoom)}px`,
              },
          // False only when the window is too small for the min layout even at this zoom, so the shell
          // overflows and #root must scroll (also the old-Firefox no-zoom path). Drives #root's overflow.
          shellFitsWindow: shellFitsIn(state.browserWidth, state.browserHeight, zoom),
          // Lets a handheld held the wrong way round be nudged to rotate rather than turned away
          shellFitsRotated: shellFitsIn(
            state.browserHeight,
            state.browserWidth,
            (zoomSupported) ? computeAutoZoom(state.browserHeight, state.browserWidth) : 1,
          ),
          portrait: (state.browserHeight > state.browserWidth),
          // Phone-sized window: the public pages lay themselves out full screen below this
          narrow: (state.browserWidth < narrowWidthPx),
          // Height left visible by the on-screen keyboard, in unzoomed px
          visualViewportHeight: state.visualViewportHeight,
          overlay: state.overlay,
          setOverlay,
        },
        mainMenu: {
          setSubMenu: setSubMenuCall,
          subMenu,
          navigatingTo,
          setNavigatingTo,
        },
        demo: {
          setDemoMode,
        },
      };

      return ret;
    },
    [
      state.browserHeight, state.browserWidth, state.visualViewportHeight,
      setSubMenuCall, subMenu,
      state.overlay, setOverlay, setDemoMode, zoomSetting, setZoomSetting,
      navigatingTo, setNavigatingTo,
    ],
  );

  return (
    <DisplayContext.Provider value={contextValue}>
      {children}
    </DisplayContext.Provider>
  );
}

DisplayProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DisplayProvider;
